import React from 'react';
import { toast } from 'react-hot-toast';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import dayjs from 'dayjs';

import { sendQuotePdfEmailApi } from '../../api/dataverse';
import { useGetNotes, useGetQuoteImage, useGetQuoteLineItems, useQuoteDetailFromDataverse } from '../../hooks';
import { QuoteStatus } from '../../types';
import { getAvailableActions } from '../../utils';
import { generateOrderAcknowledgementPDF, IPDFHeaderDates } from '@/utils/orderAcknowledgementPDF';
import { generateQuoteInvoicePDF } from '@/utils/pdfGenerator';

import { CustomerDetailsSection } from './CustomerDetailsSection';
import { PropertyImagesSection } from './PropertyImagesSection';
import { QuoteBreakdownSection } from './QuoteBreakdownSection';
import { QuoteDetailsHeader } from './QuoteDetailsHeader';
import { QuoteInternalNotes } from './QuoteInternalNotes';
import { QuoteQuickActions } from './QuoteQuickActions';
import { QuoteRecentActivity } from './QuoteRecentActivity';
import { QuoteSummarySection } from './QuoteSummarySection';
import { QuoteWindowSection } from './QuoteWindowSection';
import { SignatureSection } from './SignatureSection';
import { StatusManagementCard } from './StatusManagementCard';
import { QuoteWindowAmend } from './amend/QuoteWindowAmend';

interface QuoteDetailDropdownProps {
  quoteId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (newStatus: QuoteStatus) => void;
  onConvertToOrder?: () => void;
  onSendToCustomer?: () => void;
  onMarkAsLost?: (reason: string) => void;
  onAddNote?: (note: string) => void;
  isShowHeader?: boolean;
  onStatusSelect?: (status: QuoteStatus) => void;
  onFollowUp?: () => void;
  onCompleteFollowUp?: () => void;
  canConvertQuoteToOrder?: boolean;
  canManageQuotesStatus?: boolean;
  canManageFollowup?: boolean;
}

export const QuoteDetailDropdown: React.FC<QuoteDetailDropdownProps> = ({
  quoteId,
  isExpanded,
  onConvertToOrder,
  onSendToCustomer,
  onAddNote,
  isShowHeader = false,
  onStatusSelect,
  onFollowUp,
  onCompleteFollowUp,
  canConvertQuoteToOrder = true,
  canManageQuotesStatus = true,
  canManageFollowup = true,
}) => {
  const { data: quote, isLoading, error } = useQuoteDetailFromDataverse(quoteId);
  const { data: quoteImageResponse, isLoading: isImageLoading } = useGetQuoteImage(quoteId);
  const { data: quoteLineItems, isLoading: isLineItemsLoading } = useGetQuoteLineItems(quoteId);
  const { data: notesResponse, isLoading: isNotesLoading } = useGetNotes('quote', quoteId);

  const [isDownloadingOrderPDF, setIsDownloadingOrderPDF] = React.useState(false);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);

  if (!isExpanded) return null;

  if (isLoading || isLineItemsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <CloseCircleOutlined className="text-red-500 text-4xl mb-2" />
          <p className="text-red-600">
            {error ? 'Failed to load quote details' : 'Quote not found'}
          </p>
        </div>
      </div>
    );
  }

  const lineItems = quoteLineItems?.value || [];
  const quoteDate = quote.createdon
    ? dayjs(quote.createdon).format('DD-MM-YYYY')
    : 'N/A';

  const signatureImage = quoteImageResponse?.images?.find((img: any) =>
    img.fileName?.toLowerCase().includes('signature')
  );
  const propertyImages = quoteImageResponse?.images?.filter(
    (img: any) => !img.fileName?.toLowerCase().includes('signature')
  ) || [];

  const customerDetails = {
    name: quote['_customerid_value@OData.Community.Display.V1.FormattedValue'] || 'N/A',
    email: quote?.customerid_contact?.emailaddress1 ?? 'N/A',
    phone: quote?.customerid_contact?.telephone1 ?? 'N/A',
    address: quote?.customerid_contact?.address1_line1 || 'N/A',
    postCode: quote?.customerid_contact?.address1_postalcode || 'N/A',
    projectRef: quote?.dogma_projectreference || 'N/A',
    projectType: quote?.['dogma_projecttype@OData.Community.Display.V1.FormattedValue'] || 'N/A',
  };

  const currentStatus = quote.statuscode;
  const availableActions = getAvailableActions(currentStatus);
  const dealersCurrency = quote.dogma_DealersCurrency;
  const exchangeRate = quote.dogma_exchangerate ?? 1;

  const handleDownloadOrderAcknowledgement = async () => {
    if (!quote || lineItems.length === 0) return;
    try {
      setIsDownloadingOrderPDF(true);
      const headerDates: IPDFHeaderDates = {
        orderDate: quote.createdon ? dayjs(quote.createdon).format('DD-MMM-YYYY') : '',
        processedDate: '',
        productionDate: quote.createdon
          ? dayjs(quote.createdon).add(15, 'day').format('DD-MMM-YYYY')
          : '',
        printedDate: dayjs().format('DD-MMM-YYYY'),
      };
      await generateOrderAcknowledgementPDF(quote, lineItems, headerDates);
    } catch (err) {
    } finally {
      setIsDownloadingOrderPDF(false);
    }
  };

  const handleSendToCustomer = async () => {
    if (!quote || lineItems.length === 0) {
      toast.error('Quote data is not available');
      return;
    }
    const customerEmail = quote?.customerid_contact?.emailaddress1;
    if (!customerEmail || customerEmail === 'N/A') {
      toast.error('Customer email address is not available');
      return;
    }
    setIsSendingEmail(true);
    try {
      const pdfBase64 = (await generateQuoteInvoicePDF(quote, lineItems, true, false)) as string;
      const customerFirstName = quote?.customerid_contact?.firstname || 'Valued Customer';
      await sendQuotePdfEmailApi({
        quoteIds: [quote.quoteid],
        subject: `Your Quote #${quote.quotenumber} is Ready`,
        body: `
<p>Dear ${customerFirstName},</p>

<p>Thank you for choosing us — we're delighted to have the opportunity to work with you.</p>

<p>Please find your personalised quote attached to this email (Quote #${quote.quotenumber}). We've put together a detailed breakdown of everything discussed, so you have full visibility of what's included and the associated costs.</p>

<p>If you have any questions, would like to make adjustments, or are ready to move forward, please don't hesitate to get in touch with our team — we're happy to help.</p>

<p>We look forward to hearing from you.</p>

<p>Warm regards,<br/>
<strong>The Victorian Sliders Team</strong></p>
`.trim(),
        isHtml: true,
        attachments: [
          {
            filename: `Quote_${quote.quotenumber}.pdf`,
            content: pdfBase64,
            mimetype: 'application/pdf',
          },
        ],
      });
      toast.success('Quote sent to customer successfully!');
      onSendToCustomer?.();
    } catch (err) {
      toast.error('Failed to send quote to customer');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleAddNote = (note: string) => {
    onAddNote?.(note);
  };

  const handleStatusSelect = (newStatus: number) => {
    onStatusSelect?.(newStatus);
  };

  const showStatusCard =
    canManageQuotesStatus &&
    quote.statuscode !== QuoteStatus.CONVERTED &&
    quote.statuscode !== QuoteStatus.FOLLOW_UP_NEEDED &&
    quote.statuscode !== QuoteStatus.DRAFT;

  return (
    <div className="w-full bg-gray-50">
      {isShowHeader && (
        <QuoteDetailsHeader
          quoteNumber={quote.quotenumber ?? quoteId}
          quoteDate={quoteDate}
          quote={quote}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column — Window Configuration, Breakdown, Summary */}
        <div className="space-y-6">
          {quote.statuscode !== QuoteStatus.CONVERTED ? (
            <QuoteWindowAmend
              accountId={quote._dogma_customer_value ?? undefined}
              quoteId={quoteId}
              lineItems={lineItems}
              vatPercentage={quote.dogma_vatpercentage ?? 0}
              customDiscountPercentage={
                (quote.dogma_discountpercentage ?? 0) > 0
                  ? quote.dogma_discountpercentage!
                  : (() => {
                      const subtotal = lineItems.reduce((s, li) => s + (li.baseamount || 0), 0);
                      return subtotal > 0 ? ((quote.totaldiscountamount ?? 0) / subtotal) * 100 : 0;
                    })()
              }
              customDiscountAmount={quote.dogma_discountamount ?? undefined}
              discountType={
                (quote.dogma_discountamount ?? 0) > 0
                  ? 'amount'
                  : (quote.dogma_discountpercentage ?? 0) > 0
                    ? 'percentage'
                    : (quote.totaldiscountamount ?? 0) > 0
                      ? 'amount'
                      : 'percentage'
              }
              vssVatPercentage={(quote as any).dogma_vssvatpercentage ?? quote.dogma_vatpercentage ?? 0}
              marginPercentage={quote.dogma_marginpercentage ?? 0}
              quoteImages={quoteImageResponse?.images || []}
              companyAccount={quote.customerid_account}
            />
          ) : (
            <QuoteWindowSection
              lineItems={lineItems}
              dealersCurrency={dealersCurrency}
              exchangeRate={exchangeRate}
            />
          )}

          <QuoteBreakdownSection
            lineItems={lineItems}
            subtotal={quote.dogma_vsssubtotal ?? 0}
            discountAmount={quote.dogma_vssdiscountamount ?? 0}
            discountPercentage={quote.dogma_vssdiscountpercentage}
            dealersCurrency={dealersCurrency}
            exchangeRate={exchangeRate}
          />

          <QuoteSummarySection
            quote={quote}
            dealersCurrency={dealersCurrency}
            exchangeRate={exchangeRate}
          />
        </div>

        {/* Right Column — Customer Info, Images, Signature, Admin Actions */}
        <div className="space-y-6">
          <CustomerDetailsSection customerDetails={customerDetails} />

          <PropertyImagesSection images={propertyImages} isLoading={isImageLoading} />

          <SignatureSection
            signature={signatureImage?.imageUrl || ''}
            signedDate={quoteDate}
          />

          {showStatusCard && (
            <StatusManagementCard
              statusName={quote['statuscode@OData.Community.Display.V1.FormattedValue'] ?? ''}
              currentStatusCode={quote.statuscode}
              onStatusSelect={handleStatusSelect}
            />
          )}

          <QuoteQuickActions
            quoteId={quoteId}
            currentStatus={currentStatus}
            availableActions={availableActions}
            isShowHeader={isShowHeader}
            isDataLoading={isLoading || isLineItemsLoading}
            hasLineItems={lineItems.length > 0}
            isSendingEmail={isSendingEmail}
            isDownloadingOrderPDF={isDownloadingOrderPDF}
            canConvertQuoteToOrder={canConvertQuoteToOrder}
            canManageQuotesStatus={canManageQuotesStatus}
            canManageFollowup={canManageFollowup}
            onFollowUp={onFollowUp}
            onCompleteFollowUp={onCompleteFollowUp}
            onSendToCustomer={handleSendToCustomer}
            onConvertToOrder={onConvertToOrder}
            onDownloadPDF={handleDownloadOrderAcknowledgement}
          />

          <QuoteInternalNotes
            existingNotes={quote.description}
            notes={notesResponse?.value}
            isLoadingNotes={isNotesLoading}
            onAddNote={handleAddNote}
          />

          <QuoteRecentActivity quote={quote} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailDropdown;
